import { Injectable, Injector } from '@angular/core';
import { ConveyorStatus, CussStatusLabel } from '@app/enums/cuss-device-status.enum';
import { NEW_RELIC_EVENT } from '@app/enums/new-relic.enum';
import { NewRelicEventsService } from '@app/services/new-relic-events.service';

/**
 * ════════════════════════════════════════════════════════════════════════════
 * CI22795_DIAG — INSTRUMENTACIÓN TEMPORAL DE DIAGNÓSTICO (CI-22795)
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Objetivo: reconstruir cronológicamente el despacho de la maleta al BHS para
 * discriminar entre tres hipótesis del incidente intermitente:
 *
 *   A) `CC#RA` nunca llega al SDK  → cc_ra_attempt + cc_ra_not_sent (guardIsOK=false)
 *   B) `CC#RA` entra al SDK y falla → cc_ra_sdk_invoked + command_timeout / device_error
 *   C) confirmación prematura BHS  → cc_ra_sdk_invoked + bhs_confirmed (con snapshot AEA)
 *
 * SEMÁNTICA IMPORTANTE — lo que estos eventos SÍ y NO prueban:
 *
 * - `*_sdk_invoked` significa EXCLUSIVAMENTE que `device.sendAEA(cmd, t)` fue
 *   invocado. NO prueba que el socket transmitiera, que CUSS o el SSBD
 *   recibieran, que el firmware aceptara, ni que la banda arrancara.
 *
 * - `command_timeout` y `send_aea_response` NO traen identidad nativa del
 *   comando: el SDK entrega únicamente el nombre del método ("sendAEA") en el
 *   timeout (embross-device-manager.js, Socket.callRemoteMethod → el setTimeout
 *   invoca `listeners.get(comp).commandTimeout(method)`), y sólo el payload de
 *   respuesta en el caso correlacionado por corrId. El corrId NUNCA se expone al
 *   listener. Por eso la correlación con un comando concreto es TEMPORAL y así
 *   se etiqueta en el atributo `commandCorrelation`.
 *
 * ESTE SERVICIO NO TOMA NINGUNA DECISIÓN FUNCIONAL. Solo observa y reporta.
 * Ningún método debe lanzar: todos están protegidos, igual que
 * `DeviceMetricsService`, que es el patrón que sigue este archivo.
 *
 * ELIMINAR POR COMPLETO al cerrar CI-22795.
 * Búsqueda global para localizar toda la instrumentación: `ci22795` (case-insensitive)
 * ════════════════════════════════════════════════════════════════════════════
 */

/** Valores del atributo `diagEvent` enviado a New Relic. */
export enum Ci22795DiagEvent {
  DROP_OFF_ENTER = 'drop_off_enter',
  DELIVER_CLICK = 'deliver_click',
  PRE_CC_RA_STATE = 'pre_cc_ra_state',
  CC_RA_ATTEMPT = 'cc_ra_attempt',
  CC_RA_NOT_SENT = 'cc_ra_not_sent',
  CC_RA_SDK_INVOKED = 'cc_ra_sdk_invoked',
  ES_ATTEMPT = 'es_attempt',
  ES_NOT_SENT = 'es_not_sent',
  ES_SDK_INVOKED = 'es_sdk_invoked',
  RC_ATTEMPT = 'rc_attempt',
  RC_NOT_SENT = 'rc_not_sent',
  RC_SDK_INVOKED = 'rc_sdk_invoked',
  SEND_AEA_RESPONSE = 'send_aea_response',
  COMMAND_TIMEOUT = 'command_timeout',
  DEVICE_ERROR = 'device_error',
  DEVICE_STATUS_POLL = 'device_status_poll',
  BHS_STATE_UPDATE = 'bhs_state_update',
  /** Evidencia AEA recibida DESPUÉS de invocar CC#RA. NO significa confirmación BHS. */
  POST_CCRA_AEA_STATE = 'post_ccra_aea_state',
  /** Cambio real de deviceStatus y/o isDeviceOk. No se emite si nada cambió. */
  DEVICE_STATUS_TRANSITION = 'device_status_transition',
  BHS_CONFIRMED = 'bhs_confirmed',
  DROP_OFF_60S_TIMEOUT = 'drop_off_60s_timeout',
  DROP_OFF_ON_COMPLETE = 'drop_off_on_complete',
  DROP_OFF_COMPLETE = 'drop_off_complete',
}

/** Camino real por el que terminó el drop-off. No representa estado funcional. */
export type Ci22795CompletionReason = 'BHS_CONFIRMED' | '60S_TIMEOUT' | 'CERT_NO_DEVICE_FALLBACK';

/** Vista mínima del dispositivo AEA_BAGDROP; evita acoplarse a los tipos del SDK. */
export interface Ci22795DeviceLike {
  isDeviceOk?: boolean;
  deviceStatus?: number;
}

type DiagPayload = Record<string, string | number | boolean | null | undefined>;

/** Comandos AEA que interesan a esta HU; el resto no emite eventos para no generar ruido. */
const TRACKED_AEA_COMMANDS: ReadonlyMap<
  string,
  { attempt: Ci22795DiagEvent; notSent: Ci22795DiagEvent; sdkInvoked: Ci22795DiagEvent }
> = new Map([
  [
    'CC#RA',
    {
      attempt: Ci22795DiagEvent.CC_RA_ATTEMPT,
      notSent: Ci22795DiagEvent.CC_RA_NOT_SENT,
      sdkInvoked: Ci22795DiagEvent.CC_RA_SDK_INVOKED,
    },
  ],
  [
    'ES',
    {
      attempt: Ci22795DiagEvent.ES_ATTEMPT,
      notSent: Ci22795DiagEvent.ES_NOT_SENT,
      sdkInvoked: Ci22795DiagEvent.ES_SDK_INVOKED,
    },
  ],
  [
    'RC',
    {
      attempt: Ci22795DiagEvent.RC_ATTEMPT,
      notSent: Ci22795DiagEvent.RC_NOT_SENT,
      sdkInvoked: Ci22795DiagEvent.RC_SDK_INVOKED,
    },
  ],
]);

/** Campos AEA sanitizados que se pueden registrar. `LP` (bag tag) queda excluido a propósito. */
const SAFE_AEA_FIELDS: readonly string[] = ['SD', 'BS', 'SW', 'CW'];

/**
 * Tope de mensajes AEA persistidos a New Relic dentro de la ventana post-CC#RA.
 * Al alcanzarlo se deja de persistir (el flujo NO se altera) y se marca
 * `postCcRaAeaTruncated=true` una sola vez.
 */
const POST_CCRA_AEA_MAX = 20;

/** Valores de SD/BS que denotan una condición anómala de la banda. */
const ERROR_CONVEYOR_STATUSES: ReadonlySet<string> = new Set([
  ConveyorStatus.JAMMED,
  ConveyorStatus.FAULT,
  ConveyorStatus.STOPPED,
  ConveyorStatus.EMERGENCY,
  ConveyorStatus.INTRUSION,
]);

/**
 * Evidencia sanitizada del mensaje AEA crudo, conservando PRESENCIA (`has*`)
 * además del valor (`raw*`), para poder distinguir «campo ausente» de «campo
 * presente y vacío» — distinción que el objeto transformado pierde.
 */
interface Ci22795RawAeaSnapshot {
  sourceEventType: string;
  capturedAtMs: number;
  msSinceClickAtCapture: number | null;
  segmentCount: number;
  hasSD: boolean;
  rawSD: string | null;
  hasBS: boolean;
  rawBS: string | null;
  hasSW: boolean;
  rawSW: string | null;
  hasCW: boolean;
  rawCW: string | null;
}

/** Contexto temporal de la última invocación real al SDK (no es identidad del SDK). */
interface Ci22795SdkInvoke {
  command: string;
  requestId: string;
  atMs: number;
  timeoutMs: number;
}

@Injectable({
  providedIn: 'root',
})
export class Ci22795DiagService {
  private static readonly LOG_PREFIX = '[CI22795_DIAG][BHS_DISPATCH]';

  /** Identificador de correlación no funcional. Nunca contiene PNR, bag tag ni datos del pasajero. */
  private _diagId: string | null = null;
  /** Último diagId cerrado; permite atribuir eventos tardíos (huérfanos) a su run. */
  private _lastDiagId: string | null = null;
  private _runStartedAtMs = 0;
  private _deliverClickAtMs = 0;
  private _sdkInvokeSeq = 0;

  /**
   * Último mensaje AEA sanitizado observado en `SbdService.callback`.
   * Se captura ANTES de que ese mismo evento se propague por `listen$`, dentro
   * de la misma ejecución síncrona del callback, por lo que al leerse desde
   * `listenBHS()` corresponde al mensaje que disparó el filtro (ver
   * `rawSnapshotAgeMs`, que debe ser ~0 en ese caso).
   */
  private _lastAeaSnapshot: Ci22795RawAeaSnapshot | null = null;

  /** Última invocación real a `device.sendAEA` (cualquier comando, no solo los trackeados). */
  private _lastSdkInvoke: Ci22795SdkInvoke | null = null;

  // ── Ventana AEA post-CC#RA ──────────────────────────────────────────────────
  /** Instante de la invocación de CC#RA al SDK. Sobrevive al cierre de ventana (para timings). */
  private _ccRaSdkInvokedAtMs: number | null = null;
  /** Ventana abierta: se persisten mensajes AEA a New Relic. */
  private _postCcRaWindowOpen = false;
  private _postCcRaAeaCaptured = 0;
  private _postCcRaAeaTruncated = false;
  private _aeaSequence = 0;

  // ── Detección de transiciones de estado del dispositivo ─────────────────────
  private _lastDeviceStatus: number | null = null;
  private _lastIsDeviceOk: boolean | null = null;
  private _deviceStatusSeeded = false;

  /**
   * `NewRelicEventsService` se resuelve de forma perezosa (mismo patrón que
   * `CussService.resolveSsbdEmergencyService`). Así este servicio no agrega
   * dependencias de construcción a `SbdService` ni a los componentes que lo
   * inyectan: si New Relic no está disponible (p. ej. en specs sin proveedores),
   * la instrumentación degrada a solo consola en lugar de romper el flujo.
   */
  private _newRelicEvents: NewRelicEventsService | null = null;

  constructor(private readonly injector: Injector) {}

  /** Solo se instrumenta el stream del dispositivo mientras el drop-off está activo. */
  get isRunActive(): boolean {
    return this._diagId !== null;
  }

  get diagId(): string {
    return this._diagId ?? '-';
  }

  /** Abre una ejecución diagnóstica (una por entrada a la pantalla de drop-off). */
  startRun(): void {
    try {
      this._diagId = Math.random().toString(36).slice(2, 8).toUpperCase().padStart(6, '0');
      this._runStartedAtMs = Date.now();
      this._deliverClickAtMs = 0;
      this._sdkInvokeSeq = 0;
      this._lastAeaSnapshot = null;
      this._lastSdkInvoke = null;
      this.resetPostCcRaWindow();
      this._lastDeviceStatus = null;
      this._lastIsDeviceOk = null;
      this._deviceStatusSeeded = false;
    } catch {
      /* best-effort */
    }
  }

  /**
   * Cierra la ejecución diagnóstica. Conserva `_lastDiagId` para poder atribuir
   * eventos tardíos (el timer funcional de 60 s puede sobrevivir a `ngOnDestroy`:
   * bug PREEXISTENTE que esta instrumentación no corrige, solo etiqueta).
   */
  endRun(): void {
    this._lastDiagId = this._diagId;
    this._diagId = null;
    this._runStartedAtMs = 0;
    this._deliverClickAtMs = 0;
    this._lastAeaSnapshot = null;
    this._lastSdkInvoke = null;
    this.resetPostCcRaWindow();
    this._lastDeviceStatus = null;
    this._lastIsDeviceOk = null;
    this._deviceStatusSeeded = false;
  }

  /** Marca el instante del clic en "Entregar" para medir la latencia posterior. */
  markDeliverClick(): void {
    this._deliverClickAtMs = Date.now();
  }

  /**
   * Hito relevante: consola + New Relic.
   * Además cierra la ventana AEA post-CC#RA en los finales definidos, sin
   * temporizadores nuevos: se usa el propio flujo de eventos ya existente.
   */
  track(event: Ci22795DiagEvent, data?: DiagPayload): void {
    this.emit(event, data, true);
    // ── CI22795_DIAG ── cierre de ventana. `device_error` NO la cierra a propósito:
    // tras un 302 (caso I71Z3A) queremos seguir viendo los mensajes AEA posteriores.
    if (
      event === Ci22795DiagEvent.BHS_CONFIRMED ||
      event === Ci22795DiagEvent.DROP_OFF_60S_TIMEOUT
    ) {
      this._postCcRaWindowOpen = false;
    }
  }

  /** Evento de alta frecuencia: solo consola, para no inflar New Relic. */
  log(event: Ci22795DiagEvent, data?: DiagPayload): void {
    this.emit(event, data, false);
  }

  /**
   * Snapshot del estado que gobierna el guard `SbdService.isOK()`.
   * Recalcula `isOK` de forma defensiva (misma fórmula) para que la
   * instrumentación nunca pueda lanzar si `device` es `undefined`.
   * OJO: este valor es `snapshotIsOK`; el autoritativo es `guardIsOK`.
   */
  deviceSnapshot(device: Ci22795DeviceLike | null | undefined): DiagPayload {
    try {
      const isDeviceOk = device?.isDeviceOk ?? null;
      const deviceStatus = device?.deviceStatus ?? null;
      return {
        snapshotIsOK: isDeviceOk === true && deviceStatus !== 305,
        isDeviceOk,
        deviceStatus,
      };
    } catch {
      return { snapshotIsOK: null, isDeviceOk: null, deviceStatus: null };
    }
  }

  /**
   * Evidencia AEA cruda del último mensaje observado, para adjuntar a `BHS_CONFIRMED`.
   * `rawAeaSnapshotAvailable=false` cuando no hay snapshot correlacionable.
   * `rawSnapshotAgeMs=0` demuestra que se capturó en la MISMA ejecución síncrona
   * del callback que produjo el mensaje que disparó el filtro.
   */
  rawAeaSnapshot(): DiagPayload {
    try {
      const snap = this._lastAeaSnapshot;
      if (!snap) {
        return { rawAeaSnapshotAvailable: false };
      }
      return {
        rawAeaSnapshotAvailable: true,
        sourceEventType: snap.sourceEventType,
        rawSnapshotAgeMs: Date.now() - snap.capturedAtMs,
        rawSnapshotMsSinceClick: snap.msSinceClickAtCapture,
        aeaSegmentCount: snap.segmentCount,
        hasSD: snap.hasSD,
        rawSD: snap.rawSD,
        hasBS: snap.hasBS,
        rawBS: snap.rawBS,
        hasSW: snap.hasSW,
        rawSW: snap.rawSW,
        hasCW: snap.hasCW,
        rawCW: snap.rawCW,
      };
    } catch {
      return { rawAeaSnapshotAvailable: false };
    }
  }

  /**
   * Instrumenta el paso por el guard de `SbdService.sendAEA()`.
   * `guardIsOK` es el valor REAL que decidió el `if`, nunca una recomputación.
   * Se invoca ANTES de la llamada al SDK; no altera el guard.
   */
  trackAeaAttempt(
    command: string,
    guardIsOK: boolean,
    device: Ci22795DeviceLike | null | undefined
  ): void {
    try {
      if (!this.isRunActive) {
        return;
      }
      const mapped = TRACKED_AEA_COMMANDS.get(command);
      if (!mapped) {
        return;
      }
      // `guardIsOK` va DESPUÉS del spread: nada puede sobrescribirlo.
      const snapshot = this.deviceSnapshot(device);
      this.track(mapped.attempt, { command, ...snapshot, guardIsOK });

      if (!guardIsOK) {
        this.track(mapped.notSent, {
          command,
          reason: 'DEVICE_NOT_OK',
          ...snapshot,
          guardIsOK,
        });
      }
    } catch {
      /* best-effort */
    }
  }

  /**
   * Se invoca DESPUÉS de que `device.sendAEA(...)` ya se ejecutó.
   * SEMÁNTICA: solo prueba la INVOCACIÓN al SDK (`dispatchStage='SDK_INVOKED'`),
   * no transmisión, recepción ni movimiento físico de la banda.
   *
   * Registra el contexto de correlación temporal para TODOS los comandos
   * (incluidos BQ/SQ/CC#PA/EP), porque `command_timeout` no identifica el
   * comando: sin ese registro la correlación sería engañosa.
   */
  trackAeaSdkInvoked(command: string, timeoutMs: number): void {
    try {
      if (!this.isRunActive) {
        return;
      }
      this._sdkInvokeSeq++;
      const requestId = `${this.diagId}-${this._sdkInvokeSeq}`;
      this._lastSdkInvoke = { command, requestId, atMs: Date.now(), timeoutMs };

      // ── CI22795_DIAG ── CC#RA abre la ventana de evidencia AEA posterior.
      if (command === 'CC#RA') {
        this._ccRaSdkInvokedAtMs = Date.now();
        this._postCcRaWindowOpen = true;
        this._postCcRaAeaCaptured = 0;
        this._postCcRaAeaTruncated = false;
        this._aeaSequence = 0;
      }

      const mapped = TRACKED_AEA_COMMANDS.get(command);
      if (!mapped) {
        // Comando no trackeado: solo queda como contexto de correlación, sin PageAction.
        this.log(Ci22795DiagEvent.BHS_STATE_UPDATE, {
          note: 'untracked_sdk_invoke',
          command,
          aeaDiagRequestId: requestId,
        });
        return;
      }
      this.track(mapped.sdkInvoked, {
        command,
        timeoutMs,
        dispatchStage: 'SDK_INVOKED',
        aeaDiagRequestId: requestId,
      });
    } catch {
      /* best-effort */
    }
  }

  /**
   * Instrumenta cada evento crudo recibido desde el dispositivo durante el drop-off,
   * incluidos `sendAEA` y `commandTimeout`, que hoy no procesa el switch de `SbdService`.
   * Solo se registran campos técnicos sanitizados: nunca el mensaje AEA completo.
   */
  trackDeviceEvent(event: unknown, device: Ci22795DeviceLike | null | undefined): void {
    try {
      if (!this.isRunActive || !event) {
        return;
      }
      const key = (event as { key?: string })?.key;
      const value = (event as { value?: unknown })?.value;
      if (!key) {
        return;
      }

      switch (key) {
        case 'sendAEA':
          // Respuesta correlacionada por corrId al método `sendAEA`. El SDK NO
          // expone a qué comando AEA pertenece: solo entrega el payload.
          this.track(Ci22795DiagEvent.SEND_AEA_RESPONSE, {
            deviceEventType: key,
            response: this.toScalar(value),
            ...this.sdkInvokeCorrelation(),
            ...this.deviceSnapshot(device),
          });
          break;

        case 'commandTimeout':
          // `value` es el NOMBRE DEL MÉTODO ("sendAEA"), no el comando AEA.
          this.track(Ci22795DiagEvent.COMMAND_TIMEOUT, {
            deviceEventType: key,
            timedOutMethod: this.toScalar(value),
            ...this.sdkInvokeCorrelation(),
            ...this.deviceSnapshot(device),
          });
          break;

        case 'statusChange':
          this.trackStatusChange(value, device);
          break;

        case 'statusIsOK':
          this.trackStatusIsOK(value, device);
          break;

        case 'unsolicitedMessage':
        case 'commandCompleted':
          this.trackAeaMessage(key, value);
          break;

        default:
          this.log(Ci22795DiagEvent.BHS_STATE_UPDATE, {
            deviceEventType: key,
            value: this.toScalar(value),
          });
          break;
      }
    } catch {
      /* best-effort */
    }
  }

  /**
   * ── CI22795_DIAG (AMPLIACIÓN CI-22795) ──
   * Persiste a New Relic la evidencia AEA recibida DESPUÉS de invocar `CC#RA`,
   * únicamente mientras la ventana esté abierta y bajo el tope `POST_CCRA_AEA_MAX`.
   *
   * Se invoca desde `SbdService.callback` en los casos `unsolicitedMessage` y
   * `commandCompleted`, con el objeto YA transformado por `SsbdData.transform`
   * (el mismo que evalúa `listenBHS()`), en la MISMA ejecución síncrona del
   * callback. Así se pueden contrastar los valores parseados contra el mensaje
   * crudo (`rawSD`/`rawBS`…), que se capturó unos microsegundos antes.
   *
   * NO significa confirmación BHS: sólo prueba que llegó tráfico AEA.
   *
   * @param sourceEventType 'unsolicitedMessage' | 'commandCompleted'
   * @param listenMessage   etiqueta que `SbdService` emite por `listen$` ('getData' | 'Complete')
   * @param parsed          objeto transformado que consumirá `listenBHS()`
   */
  trackPostCcRaAeaState(
    sourceEventType: string,
    listenMessage: string,
    parsed: unknown,
    device: Ci22795DeviceLike | null | undefined
  ): void {
    try {
      if (!this.isRunActive || !this._postCcRaWindowOpen) {
        return;
      }

      if (this._postCcRaAeaCaptured >= POST_CCRA_AEA_MAX) {
        // Tope alcanzado: se deja de persistir. El flujo NO se altera.
        if (!this._postCcRaAeaTruncated) {
          this._postCcRaAeaTruncated = true;
          this.track(Ci22795DiagEvent.POST_CCRA_AEA_STATE, {
            sourceEventType,
            postCcRaAeaTruncated: true,
            postCcRaAeaCaptured: this._postCcRaAeaCaptured,
            msSinceCcRaSdkInvoke: this.msSinceCcRaSdkInvoke(),
          });
        }
        return;
      }

      this._aeaSequence++;
      this._postCcRaAeaCaptured++;
      const snap = this._lastAeaSnapshot;

      this.track(Ci22795DiagEvent.POST_CCRA_AEA_STATE, {
        sourceEventType,
        message: listenMessage,
        aeaSequence: this._aeaSequence,
        postCcRaAeaCaptured: this._postCcRaAeaCaptured,
        postCcRaAeaTruncated: false,
        msSinceCcRaSdkInvoke: this.msSinceCcRaSdkInvoke(),
        ...this.deviceSnapshot(device),

        // Mensaje AEA crudo sanitizado (presencia + valor). `rawSnapshotAgeMs=0`
        // demuestra que corresponde a este mismo mensaje (mismo turno síncrono).
        rawAeaSnapshotAvailable: !!snap,
        rawSnapshotAgeMs: snap ? Date.now() - snap.capturedAtMs : null,
        aeaSegmentCount: snap ? snap.segmentCount : null,
        hasSD: snap ? snap.hasSD : null,
        rawSD: snap ? snap.rawSD : null,
        hasBS: snap ? snap.hasBS : null,
        rawBS: snap ? snap.rawBS : null,
        hasSW: snap ? snap.hasSW : null,
        rawSW: snap ? snap.rawSW : null,
        hasCW: snap ? snap.hasCW : null,
        rawCW: snap ? snap.rawCW : null,

        // Valores YA PARSEADOS: son exactamente los operandos que evalúa `listenBHS()`.
        statusDevice: this.readParsed(parsed, 'statusDevice'),
        bagStatus: this.readParsed(parsed, 'bagStatus'),
        scaleWeight: this.readParsed(parsed, 'scaleWeight'),
        stableWeight: this.readParsed(parsed, 'stableWeight'),
        weight: this.readParsed(parsed, 'weight'),

        ...this.sdkInvokeCorrelation(),
      });
    } catch {
      /* best-effort */
    }
  }

  // ─── Privados ───────────────────────────────────────────────────────────────

  /** Milisegundos desde la invocación de CC#RA al SDK (null si aún no ocurrió). */
  private msSinceCcRaSdkInvoke(): number | null {
    return this._ccRaSdkInvokedAtMs ? Date.now() - this._ccRaSdkInvokedAtMs : null;
  }

  private resetPostCcRaWindow(): void {
    this._ccRaSdkInvokedAtMs = null;
    this._postCcRaWindowOpen = false;
    this._postCcRaAeaCaptured = 0;
    this._postCcRaAeaTruncated = false;
    this._aeaSequence = 0;
  }

  /** Lectura defensiva de un campo del objeto transformado. */
  private readParsed(parsed: unknown, key: string): string | number | boolean | null {
    try {
      return this.toScalar((parsed as Record<string, unknown> | null | undefined)?.[key]);
    } catch {
      return null;
    }
  }

  /**
   * ── CI22795_DIAG (AMPLIACIÓN CI-22795) ──
   * Emite PageAction SÓLO cuando `deviceStatus` o `isDeviceOk` cambian realmente.
   * Un poll que repite el mismo par (p. ej. 122,true → 122,true) no genera nada.
   * La primera observación del run sólo siembra el estado (DROP_OFF_ENTER ya
   * registró el valor inicial), para no emitir una transición ficticia.
   */
  private trackDeviceStatusTransition(
    sourceEventType: string,
    device: Ci22795DeviceLike | null | undefined
  ): void {
    const newDeviceStatus = device?.deviceStatus ?? null;
    const newIsDeviceOk = device?.isDeviceOk ?? null;

    if (!this._deviceStatusSeeded) {
      this._deviceStatusSeeded = true;
      this._lastDeviceStatus = newDeviceStatus;
      this._lastIsDeviceOk = newIsDeviceOk;
      return;
    }

    if (newDeviceStatus === this._lastDeviceStatus && newIsDeviceOk === this._lastIsDeviceOk) {
      return;
    }

    const previousDeviceStatus = this._lastDeviceStatus;
    const previousIsDeviceOk = this._lastIsDeviceOk;
    this._lastDeviceStatus = newDeviceStatus;
    this._lastIsDeviceOk = newIsDeviceOk;

    this.track(Ci22795DiagEvent.DEVICE_STATUS_TRANSITION, {
      sourceEventType,
      previousDeviceStatus,
      newDeviceStatus,
      previousIsDeviceOk,
      newIsDeviceOk,
      previousStatusLabel:
        previousDeviceStatus != null ? CussStatusLabel[previousDeviceStatus] ?? null : null,
      newStatusLabel: newDeviceStatus != null ? CussStatusLabel[newDeviceStatus] ?? null : null,
      msSinceCcRaSdkInvoke: this.msSinceCcRaSdkInvoke(),
      inPostCcRaWindow: this._postCcRaWindowOpen,
      ...this.sdkInvokeCorrelation(),
    });
  }

  /**
   * Correlación TEMPORAL con la última invocación al SDK. No es identidad del SDK:
   * el listener nunca recibe el corrId ni el comando AEA.
   */
  private sdkInvokeCorrelation(): DiagPayload {
    const invoke = this._lastSdkInvoke;
    if (!invoke) {
      return {
        commandCorrelation: 'UNKNOWN',
        lastTrackedCommand: null,
        aeaDiagRequestId: null,
        msSinceLastSdkInvoke: null,
      };
    }
    return {
      commandCorrelation: 'TEMPORAL_DIAGNOSTIC_CONTEXT',
      lastTrackedCommand: invoke.command,
      aeaDiagRequestId: invoke.requestId,
      msSinceLastSdkInvoke: Date.now() - invoke.atMs,
      lastSdkInvokeTimeoutMs: invoke.timeoutMs,
    };
  }

  /** `statusChange` llega como `[code, isError]`; misma normalización que `SbdService`. */
  private trackStatusChange(value: unknown, device: Ci22795DeviceLike | null | undefined): void {
    const code = Number(Array.isArray(value) ? value[0] : value);
    if (Number.isNaN(code)) {
      return;
    }
    const payload: DiagPayload = {
      deviceEventType: 'statusChange',
      errorCode: code,
      // `CussStatusLabel` es el mapping ya existente en el repo; null si el código no está catalogado.
      errorLabel: CussStatusLabel[code] ?? null,
      ...this.deviceSnapshot(device),
    };

    // 301-310 (críticos, incluye 309 SESSION_TIMEOUT), 101 MEDIA_JAMMED y 1 TIMEOUT → hito en NR.
    const isReportable = (code >= 301 && code <= 310) || code === 101 || code === 1;
    if (isReportable) {
      this.track(Ci22795DiagEvent.DEVICE_ERROR, payload);
    } else {
      this.log(Ci22795DiagEvent.DEVICE_STATUS_POLL, payload);
    }

    // ── CI22795_DIAG ── sólo emite si hubo cambio real de estado.
    this.trackDeviceStatusTransition('statusChange', device);
  }

  /** `statusIsOK` llega como "code,flag" (p. ej. "302,0"). Solo se reporta a NR cuando NO es OK. */
  private trackStatusIsOK(value: unknown, device: Ci22795DeviceLike | null | undefined): void {
    const raw = String(value ?? '');
    const parts = raw.split(',');
    const code = Number(parts[0]);
    const flag = (parts[1] ?? '').trim();
    const payload: DiagPayload = {
      deviceEventType: 'statusIsOK',
      errorCode: Number.isNaN(code) ? null : code,
      errorLabel: Number.isNaN(code) ? null : CussStatusLabel[code] ?? null,
      okFlag: flag,
      ...this.deviceSnapshot(device),
    };

    // Solo se considera "no OK" cuando la respuesta tiene el formato esperado "code,flag".
    // Un valor malformado se registra en consola pero no se reporta como error a New Relic.
    if (parts.length !== 2 || flag === '1') {
      this.log(Ci22795DiagEvent.DEVICE_STATUS_POLL, payload);
    } else {
      this.track(Ci22795DiagEvent.DEVICE_ERROR, payload);
    }

    // ── CI22795_DIAG ── sólo emite si hubo cambio real de estado.
    this.trackDeviceStatusTransition('statusIsOK', device);
  }

  /**
   * Guarda el snapshot AEA sanitizado y lo registra en consola.
   * NO emite PageAction salvo condición anómala: el snapshot viaja a New Relic
   * únicamente adjunto al evento crítico `bhs_confirmed`.
   */
  private trackAeaMessage(key: string, value: unknown): void {
    const snapshot = this.buildRawAeaSnapshot(key, value);
    this._lastAeaSnapshot = snapshot;

    const payload: DiagPayload = {
      deviceEventType: key,
      hasSD: snapshot.hasSD,
      rawSD: snapshot.rawSD,
      hasBS: snapshot.hasBS,
      rawBS: snapshot.rawBS,
      hasSW: snapshot.hasSW,
      rawSW: snapshot.rawSW,
      hasCW: snapshot.hasCW,
      rawCW: snapshot.rawCW,
    };

    const sd = snapshot.rawSD ?? '';
    const bs = snapshot.rawBS ?? '';
    if (ERROR_CONVEYOR_STATUSES.has(sd) || ERROR_CONVEYOR_STATUSES.has(bs)) {
      this.track(Ci22795DiagEvent.DEVICE_ERROR, payload);
      return;
    }
    this.log(Ci22795DiagEvent.BHS_STATE_UPDATE, payload);
  }

  /**
   * Construye la evidencia sanitizada del mensaje `#`-delimitado conservando
   * PRESENCIA y valor. Se descarta cualquier otra clave (incluida `LP`, que
   * transporta el bag tag).
   */
  private buildRawAeaSnapshot(sourceEventType: string, value: unknown): Ci22795RawAeaSnapshot {
    const found = new Map<string, string>();
    let segmentCount = 0;

    if (typeof value === 'string' && value) {
      const segments = value.split('#');
      segmentCount = segments.length;
      for (const param of segments) {
        const eqIndex = param.indexOf('=');
        if (eqIndex === -1) {
          continue;
        }
        const field = param.substring(0, eqIndex);
        if (SAFE_AEA_FIELDS.includes(field)) {
          found.set(field, param.substring(eqIndex + 1));
        }
      }
    }

    return {
      sourceEventType,
      capturedAtMs: Date.now(),
      msSinceClickAtCapture: this._deliverClickAtMs ? Date.now() - this._deliverClickAtMs : null,
      segmentCount,
      hasSD: found.has('SD'),
      rawSD: found.has('SD') ? found.get('SD') : null,
      hasBS: found.has('BS'),
      rawBS: found.has('BS') ? found.get('BS') : null,
      hasSW: found.has('SW'),
      rawSW: found.has('SW') ? found.get('SW') : null,
      hasCW: found.has('CW'),
      rawCW: found.has('CW') ? found.get('CW') : null,
    };
  }

  /** Resolución perezosa y tolerante a fallos de `NewRelicEventsService`. */
  private resolveNewRelic(): NewRelicEventsService | null {
    if (!this._newRelicEvents) {
      try {
        this._newRelicEvents = this.injector.get(NewRelicEventsService);
      } catch {
        return null;
      }
    }
    return this._newRelicEvents;
  }

  private toScalar(value: unknown): string | number | boolean | null {
    if (value === null || value === undefined) {
      return null;
    }
    const t = typeof value;
    if (t === 'string' || t === 'number' || t === 'boolean') {
      return value as string | number | boolean;
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  /** Consola siempre; New Relic solo para hitos. Nunca lanza. */
  private emit(event: Ci22795DiagEvent, data: DiagPayload | undefined, toNewRelic: boolean): void {
    try {
      const runActive = this.isRunActive;
      const base: DiagPayload = {
        diagId: runActive ? this.diagId : this._lastDiagId ?? '-',
        runActive,
        // El timer funcional de 60 s puede sobrevivir a `ngOnDestroy` (bug PREEXISTENTE):
        // esos eventos llegan fuera de run y quedan marcados como huérfanos.
        orphanAfterRun: !runActive,
        msSinceEnter: this._runStartedAtMs ? Date.now() - this._runStartedAtMs : null,
        msSinceClick: this._deliverClickAtMs ? Date.now() - this._deliverClickAtMs : null,
      };
      const payload: DiagPayload = { ...base, ...data };

      const lines = [`${Ci22795DiagService.LOG_PREFIX}[${event.toUpperCase()}]`];
      for (const [key, value] of Object.entries(payload)) {
        if (value !== undefined) {
          lines.push(`${key}=${value}`);
        }
      }
      console.warn(lines.join('\n'));

      if (toNewRelic) {
        this.resolveNewRelic()?.trackEvent(NEW_RELIC_EVENT.CI22795_BHS_DISPATCH_DIAG, {
          diagEvent: event,
          ...payload,
        });
      }
    } catch (e) {
      console.warn('[CI22795_DIAG] error emitiendo diagnóstico', e);
    }
  }
}
